import React from 'react';
import FeatherIcon from 'feather-icons-react';

export type EditableFieldType = 'text' | 'date' | 'email' | 'select' | 'boolean';

export interface SelectOption {
  value: string;
  label: string;
}

export interface EditableFieldProps {
  label: string;
  value: string | number | boolean | null | undefined;
  fieldName: string;
  isEditing: boolean;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onBlur: () => void;
  type?: EditableFieldType;
  options?: SelectOption[];
  confidenceClass?: string;
  formatValue?: (value: any) => string;
  showCopyButton?: boolean;
  onCopy?: (fieldName: string, value: string) => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  warning?: string | null; // AXIS213: Warning message for stale values
  disabled?: boolean; // AXISREF439: render as non-editable display when the field is not saveable for this referral type
}

/**
 * EditableField - A reusable component for displaying and editing field values
 * 
 * Supports:
 * - Text, date, email, select, and boolean field types
 * - Click-to-edit functionality
 * - Optional copy button
 * - Confidence-based styling
 * - Custom value formatting
 * 
 * @example
 * <EditableField
 *   label="First Name"
 *   value={referral.patient_first_name}
 *   fieldName="patient_first_name"
 *   isEditing={editingField === 'patient_first_name'}
 *   onStartEdit={() => handleStartEditing('patient_first_name')}
 *   onChange={(value) => handleFieldChange('patient_first_name', value)}
 *   onBlur={handleFieldBlur}
 *   type="text"
 *   confidenceClass={getConfidenceClass('patient_first_name')}
 *   formatValue={toCapitalCase}
 *   showCopyButton
 *   onCopy={handleCopy}
 * />
 */
export const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  fieldName,
  isEditing,
  onStartEdit,
  onChange,
  onBlur,
  type = 'text',
  options = [],
  confidenceClass = '',
  formatValue,
  showCopyButton = false,
  onCopy,
  placeholder,
  maxLength,
  className = '',
  warning = null,
  disabled = false,
}) => {
  // AXISREF440: stable automation hook for the staging E2E parity loop.
  const dataField = fieldName;
  // Format display value
  const getDisplayValue = (): string => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    
    if (type === 'boolean') {
      if (value === null) return 'N/A';
      return value === true || value === 'true' ? 'Yes' : 'No';
    }
    
    if (formatValue) {
      return formatValue(value);
    }
    
    return String(value);
  };

  // Get input value for editing
  const getInputValue = (): string => {
    if (type === 'date' && value) {
      // Convert date to YYYY-MM-DD format for date input
      const date = typeof value === 'string' ? new Date(value) : value;
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      return '';
    }
    
    if (type === 'boolean') {
      if (value === null) return '';
      return value === true || value === 'true' ? 'true' : 'false';
    }
    
    return value ? String(value) : '';
  };

  // Handle copy button click
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCopy && value !== null && value !== undefined) {
      const copyValue = formatValue ? formatValue(value) : String(value);
      onCopy(fieldName, copyValue);
    }
  };

  // Render input based on type
  const renderInput = () => {
    const inputValue = getInputValue();
    const commonProps = {
      value: inputValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => onChange(e.target.value),
      onBlur,
      className: 'form-control',
      autoFocus: true,
    };

    if (type === 'select') {
      return (
        <select {...commonProps}>
          <option value="">Select...</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (type === 'date') {
      return <input type="date" {...commonProps} />;
    }

    if (type === 'email') {
      return <input type="email" {...commonProps} placeholder={placeholder} />;
    }

    if (type === 'boolean') {
      return (
        <select {...commonProps}>
          <option value="">Not Set</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    // Default to text input
    return (
      <input
        type="text"
        {...commonProps}
        placeholder={placeholder}
        maxLength={maxLength}
      />
    );
  };

  // Render display value
  const renderDisplay = () => {
    const displayValue = getDisplayValue();
    const copyValue = value !== null && value !== undefined 
      ? (formatValue ? formatValue(value) : String(value))
      : null;

    if (showCopyButton && copyValue) {
      return (
        <div className="field-with-copy">
          <span
            className={`editable-field ${confidenceClass}`}
            onClick={disabled ? undefined : onStartEdit}
            style={disabled ? { cursor: 'default' } : undefined}
            title={disabled ? 'Not editable for this referral type' : undefined}
          >
            {displayValue}
          </span>
          <button
            className="copy-btn"
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            <FeatherIcon icon="copy" size={18} />
          </button>
        </div>
      );
    }

    return (
      <span
        className={`editable-field ${confidenceClass}`}
        onClick={disabled ? undefined : onStartEdit}
        style={disabled ? { cursor: 'default' } : undefined}
        title={disabled ? 'Not editable for this referral type' : undefined}
      >
        {displayValue}
      </span>
    );
  };

  return (
    <div className={`detail-item ${className}`} data-field={dataField}>
      <label>
        {label}:
        {/* AXIS213: Warning indicator for stale values */}
        {warning && (
          <span
            style={{
              fontSize: '0.7rem',
              padding: '2px 6px',
              backgroundColor: '#ff9800',
              color: 'white',
              borderRadius: '4px',
              marginLeft: '0.5rem',
              fontWeight: 500
            }}
            title={warning}
          >
            Stale Value
          </span>
        )}
      </label>
      {isEditing ? renderInput() : renderDisplay()}
    </div>
  );
};

